import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Skeleton,
  Paper,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Inventory2,
  AssignmentInd,
  People,
  AttachMoney,
  LocationOn,
  Warning,
  TrendingDown,
  Schedule,
  ExitToApp,
  AssignmentReturn,
  SwapHoriz,
  Groups,
  ReceiptLong,
  TrendingUp,
  RestartAlt,
  NotificationsActive,
  Today,
  Assessment,
  Refresh,
  FileDownload,
  Close,
  Inventory,
  FollowTheSigns,
  TrendingUp as TrendingUpIcon,
  Analytics,
  ShowChart,
  BarChart as BarChartIcon,
  ShoppingCart,
  FolderOpen,
  PlayArrow,
  Storefront,
  Build,
  LocalShipping,
  InboxOutlined,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useNavigate, Link } from 'react-router-dom';
import {
  useResetDemoSeedData,
  useDashboardMetrics,
  useDailyTransactionMetrics,
  useReorderAlertItems,
  useApproachingReorderItems,
  useOverdueThrItems,
  useOpenDocumentItems,
  useOpenOrderItems,
  useMissingLocationAlertSummary,
  useMissingLocationAlertItems,
  useDashboardTrend,
  usePendingIARRun,
  useLocationMetrics,
} from '@/hooks';
import type {
  ReorderAlertItem,
  ApproachingReorderItem,
  OverdueThrItem,
  OpenDocumentItem,
  OpenOrderItem,
  MissingLocationAlertItem,
  DashboardMetrics,
  DailyTrendPoint,
  LocationIemMetrics,
} from '@/hooks';
import { LoadingSkeleton } from '../components/shared/LoadingSkeleton';
import { SortableTableCell } from '@/components/shared/SortableTableCell';
import { useTableSort } from '@/hooks/useTableSort';
import { useNotifications } from '@/stores/useNotificationStore';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { useAuth } from '@/hooks/useAuth';
import { useSystemStatus } from '@/hooks/queries/admin/useSystemHealth';
import { EmptyState } from '@/components/shared/EmptyState';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import { useAllActiveCarts } from '@/hooks/queries/cart/useCartQueries';
import type { WorkflowCart } from '@/services/cart';
import { loadPrDraft } from '@/components/purchase-requisitions/CreateOrderModal';

// =============================================================================
// Types
// =============================================================================

type AlertModalType =
  | 'reorder'
  | 'approaching'
  | 'overdueThr'
  | 'openDocuments'
  | 'openOrders'
  | 'missingLocations'
  | 'savedCartsIssue'
  | 'savedCartsMaintenance'
  | 'savedCartsTransfer'
  | 'assetAnalysis'
  | 'trendChart'
  | 'locationMetrics'
  | null;

// =============================================================================
// Formatters
// =============================================================================

/** Format a number as USD currency */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/** Format a number with commas */
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

/** Format a number with up to 2 decimals */
const formatDecimal = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/** Format a date string */
const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
};

/** Format a datetime string */
const formatDateTime = (value: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

// =============================================================================
// Alert Detail Modal Component
// =============================================================================

interface AlertDetailModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  isLoading?: boolean;
}

const AlertDetailModal: React.FC<AlertDetailModalProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  isLoading,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
          px: 3,
        }}
      >
        <Typography variant="h5" component="span" fontWeight={600} fontSize="1.25rem">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      {description && (
        <Box sx={{ px: 3, py: 1.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      )}
      <DialogContent sx={{ p: 2 }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          children
        )}
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Alert Detail Tables (NO Action column per requirements)
// =============================================================================

const ReorderAlertTable: React.FC<{ items: ReorderAlertItem[]; reorderPointLabel: string }> = ({ items, reorderPointLabel }) => {
  const { sortField, sortDirection, onSort, sortedRows: sortedItems } = useTableSort(items, {
    defaultField: 'lin',
  });

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
            <SortableTableCell field="lin" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>LIN</SortableTableCell>
            <SortableTableCell field="nsn" sortField={sortField} sortDirection={sortDirection} onSort={onSort} sx={{ whiteSpace: 'nowrap' }}>NSN</SortableTableCell>
            <SortableTableCell field="item" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Nomenclature</SortableTableCell>
            <SortableTableCell field="current_qty" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
              Current Qty
            </SortableTableCell>
            <SortableTableCell field="threshold" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
              {reorderPointLabel}
            </SortableTableCell>
            <SortableTableCell field="shortage" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
              Shortage
            </SortableTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell>{item.lin ?? '-'}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.nsn ?? '-'}</TableCell>
              <TableCell>{item.item}</TableCell>
              <TableCell align="center">
                <Typography
                  component="span"
                  fontWeight={600}
                  color={item.current_qty === 0 ? 'error.main' : 'warning.main'}
                >
                  {item.current_qty}
                </Typography>
              </TableCell>
              <TableCell align="center">{item.threshold}</TableCell>
              <TableCell align="center">
                <Typography component="span" fontWeight={600} color="error.main">
                  {item.shortage}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <EmptyState icon={<Inventory fontSize="inherit" />} message={`No items at or below ${reorderPointLabel.toLowerCase()}`} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ApproachingReorderTable: React.FC<{ items: ApproachingReorderItem[]; reorderPointLabel: string }> = ({ items, reorderPointLabel }) => {
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(items);

  return (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
          <SortableTableCell field="item" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Item</SortableTableCell>
          <SortableTableCell field="lin" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>LIN</SortableTableCell>
          <SortableTableCell field="nsn" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>NSN</SortableTableCell>
          <SortableTableCell field="current_qty" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            Current Qty
          </SortableTableCell>
          <SortableTableCell field="threshold" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            {reorderPointLabel}
          </SortableTableCell>
          <SortableTableCell field="qty_to_target" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            Qty to Target
          </SortableTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((item) => (
          <TableRow key={item.id} hover>
            <TableCell>{item.item}</TableCell>
            <TableCell>{item.lin ?? '-'}</TableCell>
            <TableCell>{item.nsn ?? '-'}</TableCell>
            <TableCell align="center">
              <Typography component="span" fontWeight={600} color="warning.main">
                {item.current_qty}
              </Typography>
            </TableCell>
            <TableCell align="center">{item.threshold}</TableCell>
            <TableCell align="center">{item.qty_to_target}</TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} align="center">
              <EmptyState icon={<TrendingDown fontSize="inherit" />} message={`No items approaching ${reorderPointLabel.toLowerCase()}`} />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

const OverdueThrTable: React.FC<{ items: OverdueThrItem[] }> = ({ items }) => {
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(items);
  const person = usePersonLabels('front_window');

  return (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
          <SortableTableCell field="individual" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>{person.label}</SortableTableCell>
          <SortableTableCell field="individual_id" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>{person.individualIdLabel}</SortableTableCell>
          <SortableTableCell field="asset" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Asset</SortableTableCell>
          <SortableTableCell field="due_date" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Due Date</SortableTableCell>
          <SortableTableCell field="days_overdue" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            Days Overdue
          </SortableTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((item) => (
          <TableRow key={item.id} hover>
            <TableCell>{item.individual ?? '-'}</TableCell>
            <TableCell>{item.individual_id ?? '-'}</TableCell>
            <TableCell>{item.asset ?? '-'}</TableCell>
            <TableCell>{formatDate(item.due_date)}</TableCell>
            <TableCell align="center">
              <Typography component="span" fontWeight={600} color="error.main">
                {item.days_overdue}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} align="center">
              <EmptyState icon={<Schedule fontSize="inherit" />} message="No overdue temporary hand receipts" />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

const OpenDocumentsTable: React.FC<{ items: OpenDocumentItem[] }> = ({ items }) => {
  const theme = useTheme();
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(items);
  const person = usePersonLabels('front_window');

  return (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
          <SortableTableCell field="document_type" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Document Type</SortableTableCell>
          <SortableTableCell field="individual" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>{person.label}</SortableTableCell>
          <SortableTableCell field="individual_id" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>{person.individualIdLabel}</SortableTableCell>
          <SortableTableCell field="asset_count" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            Assets
          </SortableTableCell>
          <SortableTableCell field="comments" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Comments</SortableTableCell>
          <SortableTableCell field="created_at" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Created</SortableTableCell>
          <TableCell sx={{ fontWeight: 600 }} align="center" />
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((item) => (
          <TableRow key={item.id} hover>
            <TableCell>{item.document_type ?? '-'}</TableCell>
            <TableCell>{item.individual ?? '-'}</TableCell>
            <TableCell>{item.individual_id ?? '-'}</TableCell>
            <TableCell align="center">{item.asset_count}</TableCell>
            <TableCell
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.comments ?? '-'}
            </TableCell>
            <TableCell>{formatDateTime(item.created_at)}</TableCell>
            <TableCell align="center">
              <Link
                to={
                  item.document_type?.startsWith('Maintenance Request')
                    ? '/maintenance-request'
                    : '/asset-loss-management'
                }
                style={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.8125rem' }}
              >
                View
              </Link>
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <TableCell colSpan={7} align="center">
              <EmptyState icon={<FolderOpen fontSize="inherit" />} message="No open documents" />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

const OpenOrdersTable: React.FC<{ items: OpenOrderItem[] }> = ({ items }) => {
  const theme = useTheme();
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(items);

  return (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
          <SortableTableCell field="log_form_number" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Log Form #</SortableTableCell>
          <SortableTableCell field="order_number" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Order #</SortableTableCell>
          <SortableTableCell field="order_date" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Order Date</SortableTableCell>
          <SortableTableCell field="description" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Description</SortableTableCell>
          <SortableTableCell field="line_item_count" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            Line Items
          </SortableTableCell>
          <SortableTableCell field="total_ordered" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            Ordered
          </SortableTableCell>
          <SortableTableCell field="total_received" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
            Received
          </SortableTableCell>
          <SortableTableCell field="order_status" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Status</SortableTableCell>
          <TableCell sx={{ fontWeight: 600 }} align="center" />
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((item) => (
          <TableRow key={item.id} hover>
            <TableCell>{item.log_form_number ?? '-'}</TableCell>
            <TableCell>{item.order_number ?? '-'}</TableCell>
            <TableCell>{item.order_date ? formatDate(item.order_date) : '-'}</TableCell>
            <TableCell>{item.description ?? '-'}</TableCell>
            <TableCell align="center">{item.line_item_count}</TableCell>
            <TableCell align="center">{item.total_ordered}</TableCell>
            <TableCell align="center">{item.total_received}</TableCell>
            <TableCell>
              <Typography
                component="span"
                fontWeight={600}
                color={item.order_status === 'pending' ? 'warning.main' : 'info.main'}
              >
                {item.order_status === 'pending' ? 'Pending' : 'Partial'}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Link
                to="/purchase-requisitions"
                style={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.8125rem' }}
              >
                View
              </Link>
            </TableCell>
          </TableRow>
        ))}
        {items.length === 0 && (
          <TableRow>
            <TableCell colSpan={9} align="center">
              <EmptyState icon={<ShoppingCart fontSize="inherit" />} message="No open orders" />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
  );
};

const MissingLocationTable: React.FC<{ items: MissingLocationAlertItem[] }> = ({ items }) => {
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(items, {
    defaultField: 'entity_type',
  });

  const getCatalogItemLink = (item: MissingLocationAlertItem): string => {
    if (item.entity_type === 'catalog') {
      return `/catalog/${item.id}`;
    }

    return `/catalog/${item.catalog_id ?? item.id}`;
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
            <SortableTableCell field="entity_type" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Type</SortableTableCell>
            <SortableTableCell field="lin" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>LIN</SortableTableCell>
            <SortableTableCell field="nsn" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>NSN</SortableTableCell>
            <SortableTableCell field="display_name" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Nomenclature</SortableTableCell>
            <TableCell align="right">Update</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((item) => (
            <TableRow key={`${item.entity_type}-${item.id}`} hover>
              <TableCell sx={{ textTransform: 'capitalize' }}>{item.entity_type}</TableCell>
              <TableCell>{item.lin ?? '-'}</TableCell>
              <TableCell>{item.nsn ?? '-'}</TableCell>
              <TableCell>{item.display_name}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  component={Link}
                  to={getCatalogItemLink(item)}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  Open Item
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <EmptyState icon={<LocationOn fontSize="inherit" />} message="No items missing locations" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// =============================================================================
// 7-Day Operations Trend Chart
// =============================================================================

const SavedCartsTable: React.FC<{ carts: WorkflowCart[]; hasPrDraft: boolean }> = ({
  carts,
  hasPrDraft,
}) => {
  const theme = useTheme();
  const typeLabel = (wf: string) => {
    switch (wf) {
      case 'shipment':
        return 'Shipment';
      case 'transfer':
        return 'Transfer';
      case 'maintenance-send':
        return 'Maintenance Send';
      case 'maintenance-receive':
        return 'Maintenance Receive';
      default:
        return 'Issue';
    }
  };
  const viewLink = (wf: string) => {
    if (wf === 'transfer' || wf === 'shipment') return '/transfers-shipping';
    if (wf === 'maintenance-send' || wf === 'maintenance-receive') return '/maintenance-request';
    return '/property-book/reserved-stock';
  };

  const hasRows = carts.length > 0 || hasPrDraft;
  const { sortField, sortDirection, onSort, sortedRows: sortedCarts } = useTableSort(carts as unknown as Record<string, unknown>[]);

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
            <SortableTableCell field="workflow_type" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Type</SortableTableCell>
            <SortableTableCell field="customer_display_name" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Customer</SortableTableCell>
            <SortableTableCell field="item_count" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="center">
              Items
            </SortableTableCell>
            <SortableTableCell field="created_at" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Created</SortableTableCell>
            <SortableTableCell field="updated_at" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Last Updated</SortableTableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {hasPrDraft && (
            <TableRow hover>
              <TableCell>Purchase Requisition</TableCell>
              <TableCell>-</TableCell>
              <TableCell align="center">Draft</TableCell>
              <TableCell>-</TableCell>
              <TableCell>-</TableCell>
              <TableCell align="center">
                <Link
                  to="/purchase-requisitions"
                  style={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  View
                </Link>
              </TableCell>
            </TableRow>
          )}
          {(sortedCarts as unknown as WorkflowCart[]).map((cart) => (
            <TableRow key={cart.id} hover>
              <TableCell>{typeLabel(cart.workflow_type)}</TableCell>
              <TableCell>{cart.customer_display_name ?? '-'}</TableCell>
              <TableCell align="center">{cart.item_count ?? 0}</TableCell>
              <TableCell>{formatDateTime(cart.created_at)}</TableCell>
              <TableCell>{formatDateTime(cart.updated_at)}</TableCell>
              <TableCell align="center">
                <Link
                  to={viewLink(cart.workflow_type)}
                  style={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  View
                </Link>
              </TableCell>
            </TableRow>
          ))}
          {!hasRows && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <EmptyState icon={<InboxOutlined fontSize="inherit" />} message="No saved carts" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TrendChartContent: React.FC<{ data: DailyTrendPoint[] }> = ({ data }) => {
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');
  const theme = useTheme();
  const chartFont = theme.typography.fontFamily;
  const gridStroke = theme.palette.divider;
  const tickFill = theme.palette.text.secondary;

  // Resolve palette keys to actual hex values for Recharts
  const resolvedTrendColors = {
    issues: theme.palette.primary.main,
    turnIns: theme.palette.info.main,
    exchanges: theme.palette.success.main,
    customers: theme.palette.warning.main,
    totalValue: theme.palette.error.main,
  };

  const VALUE_KEY_MAP: Record<string, keyof DailyTrendPoint> = {
    customers: 'customersValue',
    exchanges: 'exchangesValue',
    issues: 'issuesValue',
    turnIns: 'turnInsValue',
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;
      const point: DailyTrendPoint | undefined = payload[0]?.payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            fontFamily: chartFont,
            fontSize: '0.8125rem',
            minWidth: 180,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontFamily: chartFont }}>
            {label}
          </Typography>
          {payload.map((entry: any) => {
            const valueKey = VALUE_KEY_MAP[entry.dataKey as string];
            const dollarValue = valueKey && point ? (point[valueKey] as number) : undefined;
            const isTotalValue = entry.dataKey === 'totalValue';
            return (
              <Box key={entry.dataKey} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
                <Box component="span" sx={{ color: entry.color }}>
                  {entry.name}:
                </Box>
                <Box component="span">
                  {isTotalValue
                    ? formatCurrency(Number.isFinite(entry.value) ? entry.value : 0)
                    : `${formatNumber(Number.isFinite(entry.value) ? entry.value : 0)}${dollarValue != null && entry.dataKey !== 'customers' ? `  (${formatCurrency(dollarValue)})` : ''}`}
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    },
    [chartFont],
  );
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const currencyTick = useCallback((value: number) => formatCurrency(value), []);

  const weeklyTotals = useMemo(() => ({
    issues: data.reduce((sum, d) => sum + (d.issues ?? 0), 0),
    turnIns: data.reduce((sum, d) => sum + (d.turnIns ?? 0), 0),
    exchanges: data.reduce((sum, d) => sum + (d.exchanges ?? 0), 0),
    customers: data.reduce((sum, d) => sum + (d.customers ?? 0), 0),
    totalValue: data.reduce((sum, d) => sum + (d.totalValue ?? 0), 0),
    issuesValue: data.reduce((sum, d) => sum + (d.issuesValue ?? 0), 0),
    turnInsValue: data.reduce((sum, d) => sum + (d.turnInsValue ?? 0), 0),
    exchangesValue: data.reduce((sum, d) => sum + (d.exchangesValue ?? 0), 0),
  }), [data]);

  const LEGEND_ORDER = ['customers', 'exchanges', 'issues', 'turnIns', 'totalValue'];

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const renderLegend = useCallback((props: any) => {
    const { payload } = props;
    const sorted = [...(payload ?? [])].sort(
      (a: any, b: any) => LEGEND_ORDER.indexOf(a.dataKey) - LEGEND_ORDER.indexOf(b.dataKey),
    );
    const VALUE_TOTALS: Record<string, keyof typeof weeklyTotals> = {
      exchanges: 'exchangesValue',
      issues: 'issuesValue',
      turnIns: 'turnInsValue',
    };
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', fontFamily: chartFont, fontSize: '0.8125rem' }}>
        {sorted.map((entry: any) => {
          const key = entry.dataKey as keyof typeof weeklyTotals;
          const count = weeklyTotals[key];
          const valueKey = VALUE_TOTALS[key];
          const dollarTotal = valueKey ? weeklyTotals[valueKey] : undefined;
          const isTotalValue = key === 'totalValue';
          const isCustomers = key === 'customers';
          return (
            <Box key={entry.dataKey} component="span" sx={{ color: entry.color, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, display: 'inline-block' }} />
              {entry.value}
              {isTotalValue && count != null && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  ({formatCurrency(count)})
                </Box>
              )}
              {isCustomers && count != null && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  ({formatNumber(count)})
                </Box>
              )}
              {!isTotalValue && !isCustomers && count != null && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  ({formatNumber(count)}{dollarTotal != null ? ` · ${formatCurrency(dollarTotal)}` : ''})
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }, [chartFont, weeklyTotals]);
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={(_, v: 'line' | 'bar' | null) => v && setChartType(v)}
          size="small"
        >
          <ToggleButton value="line" aria-label="line chart">
            <ShowChart fontSize="small" />
            <Box component="span" sx={{ ml: 1 }}>
              Line
            </Box>
          </ToggleButton>
          <ToggleButton value="bar" aria-label="bar chart">
            <BarChartIcon fontSize="small" />
            <Box component="span" sx={{ ml: 1 }}>
              Histogram
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <ResponsiveContainer width="100%" height={300}>
        {chartType === 'line' ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis dataKey="date" tick={{ fontFamily: chartFont, fill: tickFill }} />
            <YAxis yAxisId="count" allowDecimals={false} tick={{ fontFamily: chartFont, fill: tickFill }} />
            <YAxis
              yAxisId="value"
              orientation="right"
              tickFormatter={currencyTick}
              tick={{ fontFamily: chartFont, fill: tickFill }}
            />
            <RechartsTooltip content={CustomTooltip} />
            <Legend content={renderLegend} />
            <Line
              type="monotone"
              yAxisId="count"
              dataKey="customers"
              stroke={resolvedTrendColors.customers}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Customers"
            />
            <Line
              type="monotone"
              yAxisId="count"
              dataKey="exchanges"
              stroke={resolvedTrendColors.exchanges}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Exchanges"
            />
            <Line
              type="monotone"
              yAxisId="count"
              dataKey="issues"
              stroke={resolvedTrendColors.issues}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Issues"
            />
            <Line
              type="monotone"
              yAxisId="count"
              dataKey="turnIns"
              stroke={resolvedTrendColors.turnIns}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Turn-Ins"
            />
            <Line
              type="monotone"
              yAxisId="value"
              dataKey="totalValue"
              stroke={resolvedTrendColors.totalValue}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name="Total Value"
            />
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis dataKey="date" tick={{ fontFamily: chartFont, fill: tickFill }} />
            <YAxis yAxisId="count" allowDecimals={false} tick={{ fontFamily: chartFont, fill: tickFill }} />
            <YAxis
              yAxisId="value"
              orientation="right"
              tickFormatter={currencyTick}
              tick={{ fontFamily: chartFont, fill: tickFill }}
            />
            <RechartsTooltip content={CustomTooltip} />
            <Legend content={renderLegend} />
            <Bar
              yAxisId="count"
              dataKey="customers"
              fill={resolvedTrendColors.customers}
              name="Customers"
            />
            <Bar
              yAxisId="count"
              dataKey="exchanges"
              fill={resolvedTrendColors.exchanges}
              name="Exchanges"
            />
            <Bar yAxisId="count" dataKey="issues" fill={resolvedTrendColors.issues} name="Issues" />
            <Bar yAxisId="count" dataKey="turnIns" fill={resolvedTrendColors.turnIns} name="Turn-Ins" />
            <Bar
              yAxisId="value"
              dataKey="totalValue"
              fill={resolvedTrendColors.totalValue}
              name="Total Value"
            />
          </BarChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};

// =============================================================================
// Stock Distribution (Asset Analysis) Pie Chart
// =============================================================================

const AssetAnalysisContent: React.FC<{ metrics: DashboardMetrics }> = ({ metrics }) => {
  const theme = useTheme();
  const chartFont = theme.typography.fontFamily;
  const STOCK_COLORS = [theme.palette.success.main, theme.palette.info.main, theme.palette.error.main];
  const inStock = metrics.assets_in_stock_count;
  const onHrs = metrics.assets_on_hr_count;
  const reorderRequired = metrics.reorder_alert_count;
  const totalCount = inStock + onHrs + reorderRequired;
  const totalValue = metrics.assets_in_stock_value + metrics.assets_on_hr_value;

  const pieData = [
    { name: 'In Stock', value: inStock },
    { name: 'Assets on HRs', value: onHrs },
    { name: 'Reorder Required', value: reorderRequired },
  ];

  const pct = (n: number): string =>
    totalCount > 0 ? `${((n / totalCount) * 100).toFixed(0)}%` : '0%';

  return (
    <Box>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            /* eslint-disable @typescript-eslint/no-explicit-any */
            label={(props: any) => (
              <text
                x={props.x}
                y={props.y}
                textAnchor={props.textAnchor}
                fill={theme.palette.text.primary}
                fontSize="0.8125rem"
              >
                {`${props.name ?? 'Unknown'}: ${((props.percent ?? 0) * 100).toFixed(0)}%`}
              </text>
            )}
            /* eslint-enable @typescript-eslint/no-explicit-any */
            outerRadius={100}
            dataKey="value"
          >
            {pieData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={STOCK_COLORS[index % STOCK_COLORS.length]} />
            ))}
          </Pie>
          <RechartsTooltip
            contentStyle={{
              fontFamily: chartFont,
              backgroundColor: theme.palette.background.paper,
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
            }}
          />
          <Legend wrapperStyle={{ fontFamily: chartFont, color: theme.palette.text.primary }} />
        </PieChart>
      </ResponsiveContainer>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 4 }}>
          <Box sx={{ p: 2, bgcolor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.15 : 0.08), borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              In Stock
            </Typography>
            <Typography variant="h5" fontWeight={700} color="success.main">
              {formatNumber(inStock)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pct(inStock)} of inventory
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Box sx={{ p: 2, bgcolor: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.15 : 0.08), borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Assets on HRs
            </Typography>
            <Typography variant="h5" fontWeight={700} color="info.main">
              {formatNumber(onHrs)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pct(onHrs)} of inventory
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Box sx={{ p: 2, bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.15 : 0.08), borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Reorder Required
            </Typography>
            <Typography variant="h5" fontWeight={700} color="error.main">
              {formatNumber(reorderRequired)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {pct(reorderRequired)} of inventory
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, p: 1.5, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Total inventory: {formatNumber(totalCount)} items · Total value:{' '}
          {formatCurrency(totalValue)}
          {' · '}
          {formatNumber(metrics.total_hr_holders)} HR holder
          {metrics.total_hr_holders !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Box>
  );
};

// =============================================================================
// Location IEM Metrics Content
// =============================================================================

const LocationMetricsContent: React.FC<{ metrics: LocationIemMetrics }> = ({ metrics }) => {
  const theme = useTheme();

  const utilizationPct =
    metrics.total_locations > 0
      ? ((metrics.locations_with_stock / metrics.total_locations) * 100).toFixed(0)
      : '0';

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.15),
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Total Locations
            </Typography>
            <Typography variant="h5" fontWeight={700} color="primary.main">
              {formatNumber(metrics.total_locations)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              active locations
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.15),
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              With Stock
            </Typography>
            <Typography variant="h5" fontWeight={700} color="success.main">
              {formatNumber(metrics.locations_with_stock)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {utilizationPct}% utilization
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.15),
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Empty Locations
            </Typography>
            <Typography variant="h5" fontWeight={700} color="warning.main">
              {formatNumber(metrics.empty_locations)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              no available assets
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.15),
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Avg Assets / Location
            </Typography>
            <Typography variant="h5" fontWeight={700} color="info.main">
              {formatDecimal(metrics.avg_assets_per_location)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              across stocked locations
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 2,
          p: 1.5,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Total assets in locations: {formatNumber(metrics.total_asset_count)} &middot; Total
          value: {formatCurrency(metrics.total_asset_value)}
        </Typography>
      </Box>

      {metrics.top_locations.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Top Locations by Asset Count
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Assets
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.top_locations.map((loc) => (
                  <TableRow key={loc.location_id} hover>
                    <TableCell>{loc.breadcrumb}</TableCell>
                    <TableCell align="center">
                      <Typography component="span" fontWeight={600}>
                        {formatNumber(loc.asset_count)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(loc.asset_value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const HomePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const resetIemFrontDoor = useIemFrontDoorStore((state) => state.reset);
  const { hasRole } = useAuth();
  const isViewer = useWriteGuard();
  const isAdmin = hasRole('admin');
  const person = usePersonLabels('front_window');
  const { config: catalogConfig } = useModuleConfig('catalog');
  const reorderPointLabel = (catalogConfig.features?.dashboard_labels as Record<string, string> | undefined)?.reorder_point_column_label ?? 'Threshold';

  // Modal state
  const [openModal, setOpenModal] = useState<AlertModalType>(null);

  // Dashboard queries
  const {
    data: metrics,
    isLoading: metricsLoading,
    isError: metricsError,
    refetch: refetchMetrics,
  } = useDashboardMetrics();
  const {
    data: dailyMetrics,
    isLoading: dailyLoading,
    isError: dailyError,
    refetch: refetchDaily,
  } = useDailyTransactionMetrics();
  const resetSeedMutation = useResetDemoSeedData();
  const { data: systemStatus } = useSystemStatus();
  const demoSeedResetAvailable = systemStatus?.demoSeedResetEnabled === true;

  // Alert detail queries - enabled only when modal is open
  const { data: trendData = [], isLoading: trendLoading } = useDashboardTrend(
    openModal === 'trendChart',
  );
  const { data: reorderItems = [], isLoading: reorderLoading } = useReorderAlertItems(
    openModal === 'reorder',
  );
  const { data: approachingItems = [], isLoading: approachingLoading } = useApproachingReorderItems(
    openModal === 'approaching',
  );
  const { data: overdueThrItems = [], isLoading: overdueLoading } = useOverdueThrItems(
    openModal === 'overdueThr',
  );
  const { data: openDocumentItems = [], isLoading: documentsLoading } = useOpenDocumentItems(
    openModal === 'openDocuments',
  );
  const { data: openOrderItems = [], isLoading: ordersLoading } = useOpenOrderItems(
    openModal === 'openOrders',
  );
  const { data: missingLocationSummary, isError: missingLocationSummaryError } = useMissingLocationAlertSummary();
  const missingLocationCount = missingLocationSummary?.total_count ?? 0;
  const { data: missingLocationItems = [], isLoading: missingLocationLoading } = useMissingLocationAlertItems(
    openModal === 'missingLocations',
  );

  const { data: pendingIARRun } = usePendingIARRun();
  const { data: locationMetrics, isLoading: locationMetricsLoading } = useLocationMetrics();
  const { data: allActiveCarts = [] } = useAllActiveCarts();
  const savedCarts = allActiveCarts.filter(
    (c) => (c.item_count ?? 0) > 0,
  );
  const hasPrDraft = loadPrDraft() !== null;

  // Split saved carts by workflow type for separate status indicators
  const savedIssueOutCarts = savedCarts.filter(
    (c) => c.workflow_type === 'issue' || c.workflow_type === 'exchange' || c.workflow_type === 'turn_in',
  );
  const savedMaintenanceCarts = savedCarts.filter(
    (c) => c.workflow_type === 'maintenance-send' || c.workflow_type === 'maintenance-receive',
  );
  const savedTransferCarts = savedCarts.filter(
    (c) => c.workflow_type === 'transfer' || c.workflow_type === 'shipment',
  );

  // Refresh all dashboard data
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refetchMetrics(), refetchDaily()]);
      notifications.success('Dashboard refreshed', 'All metrics have been updated.');
    } catch {
      notifications.error('Refresh failed', 'Failed to refresh dashboard data.');
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchMetrics, refetchDaily, notifications]);

  // Export dashboard data as CSV
  const handleExport = useCallback(() => {
    const csvRows: string[] = [];

    // Header
    csvRows.push('Operational Dashboard Export');
    csvRows.push(`Export Date,${new Date().toLocaleDateString()}`);
    csvRows.push('');

    // Alerts Section
    csvRows.push('ALERTS');
    csvRows.push('Alert Type,Count');
    csvRows.push(`Reorder Required,${metrics?.reorder_alert_count ?? 0}`);
    csvRows.push(`Approaching Reorder,${metrics?.approaching_reorder_count ?? 0}`);
    csvRows.push(`Overdue THRs,${metrics?.overdue_thr_count ?? 0}`);
    csvRows.push(`Open/Suspense Documents,${metrics?.open_documents_count ?? 0}`);
    csvRows.push(`Open Orders,${metrics?.open_orders_count ?? 0}`);
    csvRows.push(`Missing Locations,${missingLocationCount}`);
    csvRows.push(`Issue Out Carts,${savedIssueOutCarts.length + (hasPrDraft ? 1 : 0)}`);
    csvRows.push(`Maintenance Carts,${savedMaintenanceCarts.length}`);
    csvRows.push(`Transfer Carts,${savedTransferCarts.length}`);
    csvRows.push('');

    // Daily Metrics Section
    csvRows.push('OPERATION STATUS DAILY');
    csvRows.push(`Metric Date,${dailyMetrics?.metric_date ?? 'N/A'}`);
    csvRows.push('Metric,Value');
    csvRows.push(`Issues,${dailyMetrics?.issues_today ?? 0}`);
    csvRows.push(`Turn-Ins,${dailyMetrics?.turn_ins_today ?? 0}`);
    csvRows.push(`Exchanges,${dailyMetrics?.exchanges_today ?? 0}`);
    csvRows.push(`Front Window Customers,${dailyMetrics?.front_window_customer_count ?? 0}`);
    csvRows.push(`Front Window Transactions,${dailyMetrics?.front_window_transaction_count ?? 0}`);
    csvRows.push(`CSR,${dailyMetrics?.csr ?? 0}`);
    csvRows.push('');

    // Key Metrics Section
    csvRows.push('KEY METRICS');
    csvRows.push('Metric,Value');
    csvRows.push(`Assets In Stock,${metrics?.assets_in_stock_count ?? 0}`);
    csvRows.push(`Assets On Hand Receipts,${metrics?.assets_on_hr_count ?? 0}`);
    csvRows.push(`HR Holders,${metrics?.total_hr_holders ?? 0}`);
    csvRows.push(`Stock Value,${metrics?.assets_in_stock_value ?? 0}`);
    csvRows.push(`Signed Out Value,${metrics?.assets_on_hr_value ?? 0}`);
    csvRows.push('');

    // Location Metrics Section
    csvRows.push('LOCATIONS KEY METRICS');
    csvRows.push('Metric,Value');
    csvRows.push(`Total Locations,${locationMetrics?.total_locations ?? 0}`);
    csvRows.push(`Locations With Stock,${locationMetrics?.locations_with_stock ?? 0}`);
    csvRows.push(`Empty Locations,${locationMetrics?.empty_locations ?? 0}`);
    csvRows.push(`Total Assets in Locations,${locationMetrics?.total_asset_count ?? 0}`);
    csvRows.push(`Total Location Asset Value,${locationMetrics?.total_asset_value ?? 0}`);
    csvRows.push(`Avg Assets Per Location,${locationMetrics?.avg_assets_per_location ?? 0}`);

    // Create and download CSV
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dashboard-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notifications.success('Export complete', 'Dashboard data exported to CSV.');
  }, [
    metrics,
    dailyMetrics,
    locationMetrics,
    missingLocationCount,
    savedIssueOutCarts.length,
    hasPrDraft,
    savedMaintenanceCarts.length,
    savedTransferCarts.length,
    notifications,
  ]);

  // Reset demo seed data
  const handleResetDemoSeedData = async () => {
    try {
      const result = await resetSeedMutation.mutateAsync();
      resetIemFrontDoor();
      notifications.success(
        'Demo data reset',
        `Reloaded ${result.seedFiles.length} demo seed files (${result.finalEntityCount} active entities).`,
      );
    } catch (mutationError) {
      const message =
        mutationError instanceof Error ? mutationError.message : 'Failed to reset demo data';
      notifications.error('Reset failed', message);
    }
  };

  // Modal handlers
  const handleOpenModal = (type: AlertModalType) => setOpenModal(type);
  const handleCloseModal = () => setOpenModal(null);

  // Loading state
  if (metricsLoading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4 }}>
        <LoadingSkeleton variant="dashboard" />
      </Container>
    );
  }

  const dailyDate = dailyMetrics?.metric_date;
  const buildTransactionLogUrl = (types: string[]): string => {
    if (!dailyDate) {
      return '/transactions';
    }
    const params = new URLSearchParams({ from: dailyDate, to: dailyDate });
    if (types.length > 0) {
      params.set('types', types.join(','));
    }
    return `/transactions?${params.toString()}`;
  };

  // Check if there are any alerts to show
  const hasAlerts =
    (metrics?.reorder_alert_count ?? 0) > 0 ||
    (metrics?.approaching_reorder_count ?? 0) > 0 ||
    (metrics?.overdue_thr_count ?? 0) > 0 ||
    missingLocationCount > 0;

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      {/* Header with Refresh and Export buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          Operational Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={isRefreshing ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
          {!isViewer && (
            <Button variant="outlined" startIcon={<FileDownload />} onClick={handleExport}>
              Export
            </Button>
          )}
        </Box>
      </Box>

      {/* Dashboard metrics error banner */}
      {(metricsError || dailyError || missingLocationSummaryError) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Some dashboard metrics failed to load. Displayed values may be incomplete.
        </Alert>
      )}

      {/* Pending IAR Banner */}
      {pendingIARRun && (
        <Alert
          severity="info"
          icon={<Inventory2 />}
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<PlayArrow />}
              component={Link}
              to={`/iar/${pendingIARRun.runId}`}
            >
              Resume IAR
            </Button>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Inventory Adjustment in Progress
          </Typography>
          <Typography variant="body2">
            {pendingIARRun.runNumber} &mdash; Started {formatDate(pendingIARRun.runDate)}
          </Typography>
        </Alert>
      )}

      {/* Quick Actions */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 1,
          bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.08 : 0.06),
          border: (t) => `1px solid ${alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.2 : 0.25)}`,
          borderTop: (t) => `3px solid ${alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.3 : 0.35)}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 'auto', whiteSpace: 'nowrap' }}>
            Quick Actions:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<Inventory />}
              onClick={() =>
                navigate('/reports', {
                  state: { reportId: 'inventory-awareness' },
                })
              }
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                px: 2,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              View Reorders
            </Button>
            <Button
              variant="contained"
              startIcon={<FollowTheSigns />}
              onClick={() =>
                navigate('/reports', {
                  state: {
                    reportId: 'overdue-assets',
                  },
                })
              }
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                px: 2,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Follow Up THRs
            </Button>
            <Button
              variant="contained"
              startIcon={<TrendingUpIcon />}
              onClick={() => handleOpenModal('trendChart')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                px: 2,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              View Trends
            </Button>
            <Button
              variant="contained"
              startIcon={<Analytics />}
              onClick={() => handleOpenModal('assetAnalysis')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                px: 2,
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Asset Analysis
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Alerts Section - only render when there are alerts */}
      {!metricsLoading && metrics && hasAlerts && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.error.main, 0.04),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <NotificationsActive sx={{ mr: 1, color: 'error.main', fontSize: 22 }} />
            <Typography variant="h6" component="h2" fontWeight={600}>
              Alerts
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {/* Reorder Alert */}
            {metrics.reorder_alert_count > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Tooltip title={`Click to view details - Catalog items at or below ${reorderPointLabel.toLowerCase()}`}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      borderLeft: 3,
                      borderColor: 'error.main',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.04) },
                    }}
                    onClick={() => handleOpenModal('reorder')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Warning sx={{ mr: 0.75, color: 'error.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={600}>
                        Reorder Required
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="error.main" fontWeight={700}>
                      {formatNumber(metrics.reorder_alert_count)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`at or below ${reorderPointLabel.toLowerCase()}`}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            )}

            {/* Approaching Reorder */}
            {metrics.approaching_reorder_count > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Tooltip title={`Click to view details - Catalog items within 20% of ${reorderPointLabel.toLowerCase()}`}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      borderLeft: 3,
                      borderColor: 'warning.main',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.04) },
                    }}
                    onClick={() => handleOpenModal('approaching')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <TrendingDown sx={{ mr: 0.75, color: 'warning.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={600}>
                        Approaching Reorder
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="warning.main" fontWeight={700}>
                      {formatNumber(metrics.approaching_reorder_count)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`within 20% of ${reorderPointLabel.toLowerCase()}`}
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            )}

            {/* Overdue THR */}
            {metrics.overdue_thr_count > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Tooltip title="Click to view details - Temporary hand receipts past their due date">
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      borderLeft: 3,
                      borderColor: 'error.main',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.04) },
                    }}
                    onClick={() => handleOpenModal('overdueThr')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Schedule sx={{ mr: 0.75, color: 'error.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={600}>
                        Overdue THRs
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="error.main" fontWeight={700}>
                      {formatNumber(metrics.overdue_thr_count)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      past due date
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            )}

            {/* Missing Locations */}
            {missingLocationCount > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Tooltip title="Click to view catalog items missing required location fields">
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      borderLeft: 3,
                      borderColor: 'warning.main',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.04) },
                    }}
                    onClick={() => handleOpenModal('missingLocations')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <LocationOn sx={{ mr: 0.75, color: 'warning.main', fontSize: 18 }} />
                      <Typography variant="body2" fontWeight={600}>
                        Missing Locations
                      </Typography>
                    </Box>
                    <Typography variant="h4" color="warning.main" fontWeight={700}>
                      {formatNumber(missingLocationCount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      catalog items need updates
                    </Typography>
                  </Box>
                </Tooltip>
              </Grid>
            )}

            {/* No more alert cards below Overdue THRs */}
          </Grid>
        </Paper>
      )}

      {/* Status Indicators */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper
          elevation={0}
          onClick={() => handleOpenModal('openDocuments')}
          sx={{
            flex: 1,
            p: 2,
            cursor: 'pointer',
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.08 : 0.06),
            border: (t) => `1px solid ${alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.2 : 0.25)}`,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {metrics?.open_documents_count ?? 0}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
            <FolderOpen sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Open/Suspense Documents
            </Typography>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          onClick={() => handleOpenModal('openOrders')}
          sx={{
            flex: 1,
            p: 2,
            cursor: 'pointer',
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.08 : 0.06),
            border: (t) => `1px solid ${alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.2 : 0.25)}`,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {metrics?.open_orders_count ?? 0}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
            <ShoppingCart sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Open Orders
            </Typography>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          onClick={() => handleOpenModal('savedCartsIssue')}
          sx={{
            flex: 1,
            p: 2,
            cursor: 'pointer',
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.08 : 0.06),
            border: (t) => `1px solid ${alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.2 : 0.25)}`,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {savedIssueOutCarts.length + (hasPrDraft ? 1 : 0)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
            <Storefront sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Issue Out Saved Carts
            </Typography>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          onClick={() => handleOpenModal('savedCartsMaintenance')}
          sx={{
            flex: 1,
            p: 2,
            cursor: 'pointer',
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.08 : 0.06),
            border: (t) => `1px solid ${alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.2 : 0.25)}`,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {savedMaintenanceCarts.length}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
            <Build sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Maintenance Saved Carts
            </Typography>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          onClick={() => handleOpenModal('savedCartsTransfer')}
          sx={{
            flex: 1,
            p: 2,
            cursor: 'pointer',
            borderRadius: 1,
            bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.08 : 0.06),
            border: (t) => `1px solid ${alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.2 : 0.25)}`,
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {savedTransferCarts.length}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
            <LocalShipping sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Transfer Saved Carts
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Operation Status Daily */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Today sx={{ mr: 1, color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" component="h2" fontWeight={600}>
            Operation Status Daily
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {/* Issues Today */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Total issue transactions processed today">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
                onClick={() => navigate(buildTransactionLogUrl(['ISSUE_OUT']))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <ExitToApp sx={{ mr: 0.5, color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Issues
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {dailyLoading ? (
                    <Skeleton width={40} />
                  ) : (
                    formatNumber(dailyMetrics?.issues_today ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Turn-Ins Today */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Total turn-in transactions processed today">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
                onClick={() => navigate(buildTransactionLogUrl(['TURN_IN']))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <AssignmentReturn sx={{ mr: 0.5, color: 'info.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Turn-Ins
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {dailyLoading ? (
                    <Skeleton width={40} />
                  ) : (
                    formatNumber(dailyMetrics?.turn_ins_today ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Exchanges Today */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Total exchange transactions processed today">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
                onClick={() => navigate(buildTransactionLogUrl(['EXCHANGE']))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <SwapHoriz sx={{ mr: 0.5, color: 'secondary.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Exchanges
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {dailyLoading ? (
                    <Skeleton width={40} />
                  ) : (
                    formatNumber(dailyMetrics?.exchanges_today ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Front Window Customers */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Distinct customers served today across Issue/Turn-In/Exchange transactions">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
                onClick={() =>
                  navigate(buildTransactionLogUrl(['ISSUE_OUT', 'TURN_IN', 'EXCHANGE']))
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Groups sx={{ mr: 0.5, color: 'success.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Customers
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {dailyLoading ? (
                    <Skeleton width={40} />
                  ) : (
                    formatNumber(dailyMetrics?.front_window_customer_count ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Front Window Transactions */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Total transactions processed today (Issue/Turn-In/Exchange)">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
                onClick={() =>
                  navigate(buildTransactionLogUrl(['ISSUE_OUT', 'TURN_IN', 'EXCHANGE']))
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <ReceiptLong sx={{ mr: 0.5, color: 'warning.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Total Txns
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {dailyLoading ? (
                    <Skeleton width={40} />
                  ) : (
                    formatNumber(dailyMetrics?.front_window_transaction_count ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* CSR */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="CSR = transactions / customers (avg transactions per customer)">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                }}
                onClick={() =>
                  navigate(buildTransactionLogUrl(['ISSUE_OUT', 'TURN_IN', 'EXCHANGE']))
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <TrendingUp sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    CSR
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {dailyLoading ? <Skeleton width={40} /> : formatDecimal(dailyMetrics?.csr ?? 0)}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Metrics */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.success.main, 0.02),
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Assessment sx={{ mr: 1, color: 'success.main', fontSize: 22 }} />
          <Typography variant="h6" component="h2" fontWeight={600}>
            Key Metrics
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {/* Total Assets on hand in inventory */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Count of assets currently available in inventory (not signed out to personnel)">
              <Box
                onClick={() => navigate('/asset')}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Inventory2 sx={{ mr: 0.5, color: 'primary.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    In Stock
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {metricsLoading ? (
                    <Skeleton width={50} />
                  ) : (
                    formatNumber(metrics?.assets_in_stock_count ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Total Assets signed out on HRs */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Count of assets currently signed out to personnel on hand receipts">
              <Box
                onClick={() => navigate('/reports')}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <AssignmentInd sx={{ mr: 0.5, color: 'info.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    On HRs
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {metricsLoading ? (
                    <Skeleton width={50} />
                  ) : (
                    formatNumber(metrics?.assets_on_hr_count ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Total HRs (individuals with hand receipts) */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title={`Number of ${person.lowerPlural} that have at least one hand receipt record`}>
              <Box
                onClick={() => navigate('/reports')}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <People sx={{ mr: 0.5, color: 'secondary.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    HR Holders
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {metricsLoading ? (
                    <Skeleton width={40} />
                  ) : (
                    formatNumber(metrics?.total_hr_holders ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Total Assets on hand $ value */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Total dollar value of assets in stock, based on catalog unit prices">
              <Box
                onClick={() => navigate('/reports')}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <AttachMoney sx={{ mr: 0.5, color: 'success.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Stock Value
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                  {metricsLoading ? (
                    <Skeleton width={70} />
                  ) : (
                    formatCurrency(metrics?.assets_in_stock_value ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Total Assets signed out $ value */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Total dollar value of assets signed out on hand receipts, based on catalog unit prices">
              <Box
                onClick={() => navigate('/reports')}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <AttachMoney sx={{ mr: 0.5, color: 'warning.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Signed Out $
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                  {metricsLoading ? (
                    <Skeleton width={70} />
                  ) : (
                    formatCurrency(metrics?.assets_on_hr_value ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>

          {/* Locations */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Tooltip title="Click to view location metrics — active locations with stock">
              <Box
                onClick={() => handleOpenModal('locationMetrics')}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <LocationOn sx={{ mr: 0.5, color: 'secondary.main', fontSize: 16 }} />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Locations
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700}>
                  {locationMetricsLoading ? (
                    <Skeleton width={40} />
                  ) : (
                    formatNumber(locationMetrics?.total_locations ?? 0)
                  )}
                </Typography>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Developer Tools — only visible to admin users in non-production environments */}
      {isAdmin && demoSeedResetAvailable && (
        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            disabled={resetSeedMutation.isPending}
            onClick={handleResetDemoSeedData}
            startIcon={
              resetSeedMutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <RestartAlt />
              )
            }
          >
            {resetSeedMutation.isPending ? 'Resetting...' : 'Reset Demo Seed Data'}
          </Button>
        </Box>
      )}

      {/* Alert Detail Modals */}
      <AlertDetailModal
        open={openModal === 'reorder'}
        onClose={handleCloseModal}
        title={`Reorder Required - Items at or Below ${reorderPointLabel}`}
        description={`The following items are at or below their ${reorderPointLabel.toLowerCase()} and need restocking.`}
        isLoading={reorderLoading}
      >
        <ReorderAlertTable items={reorderItems} reorderPointLabel={reorderPointLabel} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'approaching'}
        onClose={handleCloseModal}
        title={`Approaching Reorder - Items Within 20% of ${reorderPointLabel}`}
        description={`The following items are within 20% of their ${reorderPointLabel.toLowerCase()}.`}
        isLoading={approachingLoading}
      >
        <ApproachingReorderTable items={approachingItems} reorderPointLabel={reorderPointLabel} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'overdueThr'}
        onClose={handleCloseModal}
        title="Overdue THRs - Temporary Hand Receipts Past Due"
        description="The following items have not been returned by their due date."
        isLoading={overdueLoading}
      >
        <OverdueThrTable items={overdueThrItems} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'openDocuments'}
        onClose={handleCloseModal}
        title="Open/Suspense Documents"
        description="Documents (DA 3161, DD 200, MFR, DA 7923, DA 2407) in suspense or open status."
        isLoading={documentsLoading}
      >
        <OpenDocumentsTable items={openDocumentItems} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'trendChart'}
        onClose={handleCloseModal}
        title="7-Day Operations Trend"
        description="Daily transaction activity and estimated dollar value for the past 7 days — toggle between Line and Histogram views."
        isLoading={trendLoading}
      >
        <TrendChartContent data={trendData} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'assetAnalysis'}
        onClose={handleCloseModal}
        title="Stock Distribution Analysis"
        description="Current inventory distribution by status with percentage breakdown."
      >
        {metrics ? (
          <AssetAnalysisContent metrics={metrics} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No metrics data available.
          </Typography>
        )}
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'openOrders'}
        onClose={handleCloseModal}
        title="Open Orders - Purchase Requisitions Not Fully Received"
        description="The following purchase requisitions have outstanding items awaiting receipt."
        isLoading={ordersLoading}
      >
        <OpenOrdersTable items={openOrderItems} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'missingLocations'}
        onClose={handleCloseModal}
        title="Missing Locations"
        description="These active catalog items are missing required location fields. Open each item and set the location to resolve the alert."
        isLoading={missingLocationLoading}
      >
        <MissingLocationTable items={missingLocationItems} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'savedCartsIssue'}
        onClose={handleCloseModal}
        title="Saved Issue Out Carts"
        description="Active issue out carts with reserved items. Resume from the Issue workflow page."
      >
        <SavedCartsTable carts={savedIssueOutCarts} hasPrDraft={hasPrDraft} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'savedCartsMaintenance'}
        onClose={handleCloseModal}
        title="Saved Maintenance Carts"
        description="Active maintenance carts with items staged. Resume from the Maintenance workflow page."
      >
        <SavedCartsTable carts={savedMaintenanceCarts} hasPrDraft={false} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'savedCartsTransfer'}
        onClose={handleCloseModal}
        title="Saved Transfer Carts"
        description="Active transfer carts with items staged. Resume from the Transfers & Shipping page."
      >
        <SavedCartsTable carts={savedTransferCarts} hasPrDraft={false} />
      </AlertDetailModal>

      <AlertDetailModal
        open={openModal === 'locationMetrics'}
        onClose={handleCloseModal}
        title="Locations Key Metrics"
        description="Warehouse location utilization, asset distribution, and top stocked locations."
        isLoading={locationMetricsLoading}
      >
        {locationMetrics ? (
          <LocationMetricsContent metrics={locationMetrics} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No location metrics data available.
          </Typography>
        )}
      </AlertDetailModal>
    </Container>
  );
};
